"use client";
import { type FC, type PropsWithChildren, useCallback, useState } from "react";
import {
	type FreshdeskTag,
	ServiceContext,
	getDomainDisplayNames,
	separateLocale,
	useKeydown,
} from "../..";

type Props = PropsWithChildren<{
	lang: string;
	token?: string;
	storeName?: string;
	isOpen: boolean;
	user: {
		firstname?: string;
		lastname?: string;
		email?: string;
		phone?: string;
		isBusiness?: boolean;
		cartId?: string;
	};
	createSharableCart?: () => Promise<string | null>;
}>;

export const ServiceContextProvider: FC<Props> = (props) => {
	const { lang, children, token, storeName, isOpen, user, createSharableCart } = props;
	const { language } = separateLocale(lang);
	const chatAvailable = token !== undefined && token !== " " && isOpen;

	const [isLoading, setIsLoading] = useState(false);
	const [chatActive, setChatActive] = useState(false);
	const [unreadCount, setUnreadCount] = useState(0);
	const [tags, setTags] = useState<FreshdeskTag[]>([]);

	const chatHandler = useCallback(async () => {
		if (window.fcWidget) {
			if (!window.fcWidget.isLoaded()) {
				setIsLoading(true);

				await window.fcWidget.init({
					token: token,
					host: "https://wchat.freshchat.com",
					locale: language,
					config: {
						headerProperty: {
							hideChatButton: true,
						},
					},
					tags,
				});

				const sharedCartLink = await createSharableCart?.();

				window.fcWidget.user.setProperties({
					locale: language,
					firstName: user.firstname,
					lastName: user.lastname,
					email: user.email,
					phone: user.phone,

					"Volledige naam":
						[user?.firstname, user?.lastname].filter(Boolean).join(" ") ?? "Onbekend",
					"E-mailadres": user.email ?? "Onbekend",
					Telefoonnummer: user.phone ?? "Onbekend",
					"Zakelijk account": user.isBusiness ? "Ja" : "Nee",
					Website: storeName ?? "Onbekend",
					"Quote ID": user.cartId ?? "Onbekend",
					Winkelwagen: sharedCartLink ?? "Onbekend",
					Taal: getDomainDisplayNames(lang).language ?? "Onbekend",
				});
			}

			if (window.fcWidget.isOpen()) {
				window.fcWidget.close();
			} else {
				window.fcWidget.open();
			}

			window.fcWidget.on("widget:loaded", () => window.fcWidget?.open());
			window.fcWidget.on("widget:opened", () => setIsLoading(false));
			window.fcWidget.on("unreadCount:notify", (resp: { count?: number }) =>
				setUnreadCount(Number(resp?.count)),
			);
		}
	}, [lang, storeName, token, language, user, tags, createSharableCart]);

	const onOpenHandler = useCallback(
		(open?: boolean) => {
			if (window.fcWidget?.isOpen()) {
				window.fcWidget.close();
			}

			setChatActive(open ?? !chatActive);
		},
		[chatActive],
	);

	useKeydown(() => onOpenHandler(false));

	return (
		<ServiceContext.Provider
			value={{
				chatAvailable,

				isLoading,
				chatActive,
				unreadCount,

				onOpenHandler,
				chatHandler,

				tags,
				setTags,
			}}
		>
			{children}

			{chatAvailable && <script async src="https://wchat.freshchat.com/js/widget.js" />}
		</ServiceContext.Provider>
	);
};
